import React, { useEffect, useState } from "react";
import {
  initialNodeStyle,
  initialDirectedEdgeStyle,
  initialUndirectedEdgeStyle,
  visitedNodeStyle,
  visitedDirectedEdgeStyle,
  visitedUndirectedEdgeStyle,
  visitingNodeStyle,
} from "./GraphStyle";
import "./DFSAlgorithm.css";
import * as d3 from "d3";

const DFSAlgorithm = ({ cytoscape, nodes, isDirected }) => {
  const [visitedNodes, setVisitedNodes] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentNode, setCurrentNode] = useState(null);
  const [stack, setStack] = useState([]);

  const initialEdgeStyleToApply = isDirected
    ? initialDirectedEdgeStyle
    : initialUndirectedEdgeStyle;

  const visitedEdgeStyleToApply = isDirected
    ? visitedDirectedEdgeStyle
    : visitedUndirectedEdgeStyle;

  useEffect(() => {
    // Clear visited nodes when the graph type changes
    setVisitedNodes([]);
  }, [isDirected]);

  let visitedNodeCount = 0;
  let visitedNodesSet = new Set();

  const depthFirstSearch = async (
    startNode,
    parentNode = null,
    visitedNodesSet,
    stack
  ) => {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const currentNode = cytoscape.getElementById(startNode.data.id);
    currentNode.style(visitingNodeStyle);

    let adjacentEdges;

    if (isDirected) {
      // For directed graphs, consider only outgoing edges
      adjacentEdges = Array.from(
        cytoscape.edges(`[source="${startNode.data.id}"]`)
      );
    } else {
      // For undirected graphs, consider all adjacent edges
      adjacentEdges = Array.from(
        cytoscape.edges(
          `[source="${startNode.data.id}"], [target="${startNode.data.id}"]`
        )
      );
    }

    adjacentEdges.sort((edgeA, edgeB) => {
      const idA = edgeA.data("id");
      const idB = edgeB.data("id");
      return idA.localeCompare(idB);
    });

    for (const edge of adjacentEdges) {
      const sourceId = edge.source().id();
      const targetId = edge.target().id();
      const adjacentNodeId =
        sourceId === startNode.data.id ? targetId : sourceId;
      if (adjacentNodeId !== parentNode) {
        const adjacentNode = nodes.find(
          (node) => node.data.id === adjacentNodeId
        );
        if (!visitedNodesSet.has(adjacentNode.data.id)) {
          stack.push(adjacentNode.data.id);
          setStack([...stack]);
          visitedNodesSet.add(adjacentNode.data.id);
          setVisitedNodes([...visitedNodes, adjacentNode.data.id]);
          edge.style(
            isDirected ? visitedDirectedEdgeStyle : visitedUndirectedEdgeStyle
          );
          await depthFirstSearch(
            adjacentNode,
            startNode.data.id,
            visitedNodesSet,
            stack
          );
        }
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 2000));
    stack.pop(currentNode);
    setStack([...stack]);
    currentNode.style(visitedNodeStyle);
  };

  const animateDFS = async () => {
    if (isAnimating) return;
    setIsAnimating(true);

    const sortedNodes = [...nodes].sort(
      (a, b) => parseInt(a.data.id) - parseInt(b.data.id)
    );

    for (const startNode of sortedNodes) {
      if (!visitedNodesSet.has(startNode.data.id)) {
        stack.push(startNode.data.id);
        setStack([...stack]);
        visitedNodesSet.add(startNode.data.id);
        setVisitedNodes([...visitedNodes, startNode.data.id]);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await depthFirstSearch(
          startNode,
          visitedNodeCount,
          visitedNodesSet,
          stack
        );
      }
    }

    setIsAnimating(false);
  };

  const resetAnimation = () => {
    if (isAnimating) return;

    // Reset styles of all nodes and edges
    cytoscape.elements("node").style(initialNodeStyle);

    // Reset styles of all edges to initialEdgeStyleToApply
    cytoscape.elements("edge").style(initialEdgeStyleToApply);

    // Reset the state
    setVisitedNodes([]);
    setStack([]);
    visitedNodesSet.clear();

    setIsAnimating(false);
  };

  /*useEffect(() => {
    setVisitedNodes([]);
  }, []);

  useEffect(() => {
    if (cytoscape) {
      const stylesheet = [
        {
          selector: "node",
          style: {
            ...initialNodeStyle,
          },
        },
        {
          selector: "edge",
          style: {
            ...initialEdgeStyleToApply,
          },
        },
        {
          selector: visitedNodes
            .map((visitedNodeId) => `node[id="${visitedNodeId}"]`)
            .join(", "),
          style: {
            ...visitedNodeStyle,
          },
        },
        {
          selector: visitedNodes
            .map((visitedNodeId) => `edge[source="${visitedNodeId}"]`)
            .join(", "),
          style: {
            ...visitedEdgeStyleToApply,
          },
        },
      ];

      cytoscape.style().fromJson(stylesheet).update();
    }
  }, [cytoscape, visitedNodes]);*/

  return (
    <div>
      <div className="button-container">
        <button
          className="animate-reset-button"
          onClick={animateDFS}
          disabled={isAnimating}
        >
          {isAnimating ? "Animating..." : "Animate DFS"}
        </button>
        &nbsp;&nbsp;
        <button
          className="animate-reset-button"
          onClick={resetAnimation}
          disabled={isAnimating}
        >
          Reset Animation
        </button>
      </div>
      <div>
        {/* Display the stack */}
        <div className="stack-container">
          <div className="stack-title">Stack:</div> &nbsp;
          <ul className="stack">
            {[...stack].reverse().map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DFSAlgorithm;
